// src/components/Dashboard/PolicyReview.jsx
//
// Módulo `policyReview` del paso "Revisión de póliza": subir el PDF,
// analizarlo con IA (Gemini, vía api/policy-analyze.php) o llenarlo a mano,
// revisar el borrador y confirmarlo. Lo propuesto por la IA NUNCA pasa al
// reclamo sin que una persona pulse "Confirmar revisión".
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { openBackup } from '../../services/backupService';
import {
  analyzePolicy, buildManualPolicyPrompt, CLAUSE_GROUPS, confirmPolicyReview, draftFromClaim, getPolicyReview,
  normalizePolicyDraft, parsePolicyAnswer, savePolicyDraft, uploadPolicyFile, removePolicyFile
} from '../../services/policyService';
import './ClaimFlow.css';

const CLAUSE_LABELS = {
  exclusions: ['Exclusiones', 'Exclusions'],
  limitations: ['Limitaciones', 'Limitations'],
  conditions: ['Condiciones', 'Conditions'],
  endorsements: ['Endosos', 'Endorsements']
};

const money = value => (value === null || value === undefined || value === ''
  ? '—'
  : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }));
const pageRef = page => (page ? ` · p. ${page}` : '');
const inputValue = value => (value === null || value === undefined ? '' : value);

// Filas editables de una lista (coberturas, deducibles, cláusulas…).
const ListEditor = ({ items, columns, onChange, addLabel, empty, es }) => {
  const update = (index, key, value) => onChange(items.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  return (
    <div className="policy-list">
      {items.length === 0 && <div className="flow-note">{empty}</div>}
      {items.map((item, index) => (
        <div key={index} className="policy-list-row">
          {columns.map(col => (
            <label key={col.key} className={`policy-cell ${col.wide ? 'is-wide' : ''} ${col.narrow ? 'is-narrow' : ''}`}>
              <span className="cc-label">{col.label}</span>
              {col.multiline
                ? <textarea className="cc-input" rows={2} value={inputValue(item[col.key])} onChange={e => update(index, col.key, e.target.value)} />
                : <input className="cc-input" type={col.type || 'text'} inputMode={col.type === 'number' ? 'decimal' : undefined} value={inputValue(item[col.key])} onChange={e => update(index, col.key, e.target.value)} />}
            </label>
          ))}
          <button type="button" className="cd-btn policy-remove" aria-label={`${es ? 'Quitar' : 'Remove'} ${item[columns[0].key] || item[columns[1]?.key] || `#${index + 1}`}`} onClick={() => onChange(items.filter((_, i) => i !== index))}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      ))}
      <button type="button" className="cd-btn policy-add" onClick={() => onChange([...items, Object.fromEntries(columns.map(c => [c.key, c.type === 'number' ? null : '']))])}>
        <i className="fa-solid fa-plus"></i> {addLabel}
      </button>
    </div>
  );
};

// Resumen del análisis (sin editar): lo usan la Revisión de póliza y Documentos.
export const PolicySummary = ({ shown, es }) => {
  const t = pair => (es ? pair[0] : pair[1]);
  return (
    <div className="flow-subpanel">
          <div className="closing-item-amounts">
            <span>{es ? 'Aseguradora' : 'Insurance company'}<strong>{shown.insuranceCompanyName || '—'}</strong></span>
            <span>{es ? 'Póliza' : 'Policy'}<strong>{shown.policyNumber || '—'}</strong></span>
            <span>{es ? 'Tipo' : 'Form'}<strong>{shown.policyForm || '—'}</strong></span>
            <span>{es ? 'Vigencia' : 'Term'}<strong>{shown.policyEffectiveDate || '—'} → {shown.policyExpirationDate || '—'}</strong></span>
          </div>
          {shown.coverages.length > 0 && (
            <div className="closing-totals">
              {shown.coverages.map((c, i) => (
                <div key={i} className="closing-total"><span>{c.code ? `Cov. ${c.code} · ` : ''}{c.name}{pageRef(c.page)}</span><strong>{money(c.limit)}</strong></div>
              ))}
            </div>
          )}
          {shown.deductibles.length > 0 && (
            <ul className="policy-clauses">
              {shown.deductibles.map((d, i) => (
                <li key={i}><strong>{es ? 'Deducible' : 'Deductible'} {d.type}:</strong> {d.amount !== null ? money(d.amount) : ''}{d.percent !== null ? ` ${d.percent}%` : ''}<small>{pageRef(d.page)}</small></li>
              ))}
            </ul>
          )}
          {CLAUSE_GROUPS.filter(g => shown[g].length > 0).map(group => (
            <details key={group} className="policy-details">
              <summary>{t(CLAUSE_LABELS[group])} ({shown[group].length})</summary>
              <ul className="policy-clauses">
                {shown[group].map((c, i) => <li key={i}><strong>{c.title}</strong><small>{pageRef(c.page)}</small><div>{c.summary}</div></li>)}
              </ul>
            </details>
          ))}
    </div>
  );
};

const PolicyReview = ({ claimRecord, language, onClaimChange }) => {
  const es = language === 'es';
  const t = pair => (es ? pair[0] : pair[1]);
  const [review, setReview] = useState(null); // stepData/policy_review
  const [editing, setEditing] = useState(null); // borrador en edición
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0); // para vaciar el selector tras subir
  const [busy, setBusy] = useState('');
  const [manualAnswer, setManualAnswer] = useState('');
  const [promptText, setPromptText] = useState(''); // si el portapapeles falla, se muestra para copiar a mano

  useEffect(() => {
    let cancelled = false;
    getPolicyReview(claimRecord.id)
      .then(data => { if (!cancelled) setReview(data || {}); })
      .catch(error => { console.error('Error loading policy review:', error); if (!cancelled) setReview({}); });
    return () => { cancelled = true; };
  }, [claimRecord.id]);

  if (!review) return <div className="flow-note">{es ? 'Cargando revisión de póliza…' : 'Loading policy review…'}</div>;

  const run = async (label, task) => {
    setBusy(label);
    try { await task(); } catch (error) {
      console.error('Policy review action failed:', error);
      toast.error(error.message);
    } finally { setBusy(''); }
  };

  const upload = () => run('upload', async () => {
    const fileRef = await uploadPolicyFile(claimRecord.id, file);
    setReview(r => ({ ...r, file: fileRef }));
    setFile(null);
    setFileInputKey(k => k + 1);
    if (onClaimChange) onClaimChange({}); // refresca Documentos
    toast.success(es ? 'Póliza respaldada en el reclamo' : 'Policy backed up to the claim');
  });

  const removeFile = () => {
    if (!window.confirm(es ? '¿Quitar esta póliza del reclamo? Podrás subir otra.' : 'Remove this policy from the claim? You can upload another one.')) return;
    return run('remove', async () => {
      await removePolicyFile(claimRecord.id);
      setReview(r => ({ ...r, file: null }));
      if (onClaimChange) onClaimChange({});
      toast.success(es ? 'Póliza quitada' : 'Policy removed');
    });
  };

  const analyze = () => run('analyze', async () => {
    const result = await analyzePolicy(review.file.storageKey);
    const meta = { source: 'ai', model: result.model || '', analyzedAt: result.analyzedAt || new Date().toISOString(), analyzedBy: result.analyzedBy || '', simulated: Boolean(result.simulated) };
    const saved = await savePolicyDraft(claimRecord.id, result.draft, meta);
    setReview(r => ({ ...r, ...saved }));
    setEditing(saved.draft);
    toast.success(es ? 'Borrador listo: revísalo antes de confirmar' : 'Draft ready: review it before confirming');
  });

  // Respaldo: usar cualquier otra IA con las mismas instrucciones.
  const copyPrompt = () => run('copy', async () => {
    const text = await buildManualPolicyPrompt();
    try {
      await navigator.clipboard.writeText(text);
      setPromptText('');
      toast.success(es ? 'Instrucciones copiadas: pégalas en la otra IA junto con el PDF' : 'Instructions copied: paste them in the other AI with the PDF');
    } catch (error) {
      setPromptText(text);
      toast(es ? 'Cópialas del cuadro de abajo' : 'Copy them from the box below');
    }
  });

  const loadManualAnswer = () => run('paste', async () => {
    const draft = parsePolicyAnswer(manualAnswer);
    const meta = { source: 'ai', model: es ? 'otra IA (a mano)' : 'other AI (manual)', analyzedAt: new Date().toISOString(), analyzedBy: '', simulated: false };
    const saved = await savePolicyDraft(claimRecord.id, draft, meta);
    setReview(r => ({ ...r, ...saved }));
    setEditing(saved.draft);
    setManualAnswer('');
    toast.success(es ? 'Borrador cargado: revísalo antes de confirmar' : 'Draft loaded: review it before confirming');
  });

  const startManual = () => setEditing(review.draft ? normalizePolicyDraft(review.draft) : draftFromClaim(claimRecord));

  const saveDraft = () => run('save', async () => {
    const saved = await savePolicyDraft(claimRecord.id, editing, review.source ? {} : { source: 'manual' });
    setReview(r => ({ ...r, ...saved, source: r.source || 'manual' }));
    setEditing(saved.draft);
    toast.success(es ? 'Borrador guardado' : 'Draft saved');
  });

  const confirm = () => run('confirm', async () => {
    const { review: confirmed, claimChanges } = await confirmPolicyReview(claimRecord, editing);
    setReview(r => ({ ...r, ...confirmed }));
    setEditing(null);
    if (onClaimChange && Object.keys(claimChanges).length) onClaimChange(claimChanges);
    toast.success(es ? 'Revisión confirmada; datos pasados al reclamo' : 'Review confirmed; data copied to the claim');
  });

  const set = (key, value) => setEditing(d => ({ ...d, [key]: value }));

  const statusBadge = review.status === 'reviewed'
    ? <span className="cd-status-badge cd-status-paid">{es ? 'Revisada' : 'Reviewed'} ✓</span>
    : review.status === 'draft'
      ? <span className="cd-status-badge cd-status-pending">{review.source === 'ai' ? (es ? 'Borrador de IA · sin revisar' : 'AI draft · not reviewed') : (es ? 'Borrador · sin confirmar' : 'Draft · not confirmed')}</span>
      : <span className="cd-status-badge cd-status-pending">{es ? 'Sin revisar' : 'Not reviewed'}</span>;

  const shown = review.status === 'reviewed' ? normalizePolicyDraft(review.data) : review.draft ? normalizePolicyDraft(review.draft) : null;

  return (
    <div className="flow-block policy-review">
      <div className="closing-head">
        <div className="closing-head-title">{es ? 'Revisión de póliza' : 'Policy review'}</div>
        {statusBadge}
      </div>

      {/* 1. El PDF de la póliza */}
      <div className="doc-signature">
        {review.file ? (
          <>
            <span className="doc-signature-meta"><i className="fa-solid fa-file-pdf"></i> {review.file.name}</span>
            <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(review.file.storageKey).catch(e => toast.error(e.message))}>
              <i className="fa-solid fa-eye"></i> {es ? 'Ver póliza' : 'View policy'}
            </button>
            <button type="button" className="cd-btn" disabled={Boolean(busy)} onClick={removeFile}>
              <i className="fa-solid fa-trash"></i> {busy === 'remove' ? (es ? 'Quitando…' : 'Removing…') : (es ? 'Quitar póliza' : 'Remove policy')}
            </button>
          </>
        ) : <span className="doc-signature-meta">{es ? 'Todavía no se subió la póliza.' : 'Policy not uploaded yet.'}</span>}
      </div>
      <div className="policy-upload">
        <input key={fileInputKey} type="file" accept="application/pdf" className="cc-input" aria-label={es ? 'PDF de la póliza' : 'Policy PDF'} onChange={e => setFile(e.target.files?.[0] || null)} />
        <button type="button" className="cd-btn" disabled={!file || Boolean(busy)} onClick={upload}>
          <i className="fa-solid fa-upload"></i> {busy === 'upload' ? (es ? 'Subiendo…' : 'Uploading…') : review.file ? (es ? 'Reemplazar póliza' : 'Replace policy') : (es ? 'Subir póliza' : 'Upload policy')}
        </button>
      </div>

      {/* 2. Analizar o llenar a mano */}
      {!editing && (
        <div className="flow-actions">
          <button type="button" className="cd-btn cd-btn-check" disabled={!review.file || Boolean(busy)} title={!review.file ? (es ? 'Primero sube la póliza' : 'Upload the policy first') : undefined} onClick={analyze}>
            <i className="fa-solid fa-wand-magic-sparkles"></i> {busy === 'analyze' ? (es ? 'Analizando… (puede tardar 1 minuto)' : 'Analyzing… (up to a minute)') : (es ? 'Analizar con IA' : 'Analyze with AI')}
          </button>
          <button type="button" className="cd-btn" disabled={Boolean(busy)} onClick={startManual}>
            <i className="fa-solid fa-pen"></i> {review.status ? (es ? 'Editar revisión' : 'Edit review') : (es ? 'Llenar a mano' : 'Fill in manually')}
          </button>
        </div>
      )}
      {!editing && (
        <details className="policy-details policy-manual">
          <summary>{es ? '¿La IA no responde? Hazlo con otra IA (ChatGPT, Claude, Gemini…)' : 'AI not responding? Use another AI (ChatGPT, Claude, Gemini…)'}</summary>
          <ol className="policy-manual-steps">
            <li>
              {es ? 'Copia las instrucciones (son las mismas que usa el sistema).' : 'Copy the instructions (same as the system uses).'}
              <button type="button" className="cd-btn" disabled={Boolean(busy)} onClick={copyPrompt}>
                <i className="fa-solid fa-copy"></i> {es ? 'Copiar instrucciones' : 'Copy instructions'}
              </button>
              {promptText && <textarea className="cc-input" rows={6} readOnly value={promptText} aria-label={es ? 'Instrucciones para copiar' : 'Instructions to copy'} onFocus={e => e.target.select()} />}
            </li>
            <li>{es ? 'En la otra IA, adjunta el PDF de la póliza y pega las instrucciones.' : 'In the other AI, attach the policy PDF and paste the instructions.'}</li>
            <li>
              {es ? 'Copia toda su respuesta, pégala aquí y carga el borrador.' : 'Copy its full answer, paste it here and load the draft.'}
              <textarea className="cc-input" rows={5} value={manualAnswer} aria-label={es ? 'Respuesta de la otra IA' : 'Answer from the other AI'} placeholder={es ? 'Pega aquí la respuesta de la IA…' : 'Paste the AI answer here…'} onChange={e => setManualAnswer(e.target.value)} />
              <button type="button" className="cd-btn cd-btn-check" disabled={Boolean(busy) || !manualAnswer.trim()} onClick={loadManualAnswer}>
                <i className="fa-solid fa-file-import"></i> {es ? 'Cargar borrador' : 'Load draft'}
              </button>
            </li>
          </ol>
          <p className="flow-note">{es ? 'Ojo: la póliza tiene datos personales; usa una cuenta de trabajo de la IA, no una personal.' : 'Note: the policy has personal data; use a work AI account, not a personal one.'}</p>
        </details>
      )}
      {review.analyzedAt && (
        <div className="flow-note">
          {es ? 'Último análisis' : 'Last analysis'}: {new Date(review.analyzedAt).toLocaleString(es ? 'es' : 'en')}{review.model ? ` · ${review.model}` : ''}
          {review.status === 'reviewed' && review.reviewedBy && ` · ${es ? 'revisada por' : 'reviewed by'} ${review.reviewedBy}`}
        </div>
      )}

      {/* 3. Resumen (sin editar) */}
      {!editing && shown && (
        <PolicySummary shown={shown} es={es} />
      )}

      {/* 4. Editor del borrador */}
      {editing && (
        <div className="flow-subpanel">
          {review.source === 'ai' && review.status !== 'reviewed' && (
            <div className="closing-warning">
              {es ? 'Propuesto por IA: compáralo con la póliza (cada dato indica su página) antes de confirmar.' : 'Proposed by AI: check it against the policy (each item shows its page) before confirming.'}
              {review.simulated && <strong> {es ? 'Resultado simulado del emulador.' : 'Simulated emulator result.'}</strong>}
            </div>
          )}
          {editing.warnings.length > 0 && (
            <ul className="closing-warning policy-warnings">{editing.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          )}

          <div className="cc-grid">
            {[
              ['insuranceCompanyName', ['Aseguradora', 'Insurance company']],
              ['policyNumber', ['Número de póliza', 'Policy number']],
              ['policyForm', ['Tipo de póliza (HO-3, DP-3…)', 'Policy form (HO-3, DP-3…)']],
              ['agentAgency', ['Agente / agencia', 'Agent / agency']],
              ['policyEffectiveDate', ['Vigente desde', 'Effective'], 'date'],
              ['policyExpirationDate', ['Vence', 'Expires'], 'date'],
              ['propertyAddress', ['Propiedad asegurada', 'Insured property']]
            ].map(([key, label, type]) => (
              <div key={key} className="cc-input-group">
                <label className="cc-label">{t(label)}</label>
                <input className="cc-input" type={type || 'text'} value={editing[key]} onChange={e => set(key, e.target.value)} />
              </div>
            ))}
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'Asegurados nombrados (separados por coma)' : 'Named insureds (comma separated)'}</label>
              <input className="cc-input" value={editing.namedInsureds.join(', ')} onChange={e => set('namedInsureds', e.target.value.split(',').map(s => s.trimStart()))} />
            </div>
          </div>

          <div className="flow-block-label">{es ? 'Coberturas y límites' : 'Coverages & limits'}</div>
          <ListEditor es={es} items={editing.coverages} onChange={v => set('coverages', v)} addLabel={es ? 'Agregar cobertura' : 'Add coverage'} empty={es ? 'Sin coberturas.' : 'No coverages.'}
            columns={[{ key: 'code', label: es ? 'Cód.' : 'Code', narrow: true }, { key: 'name', label: es ? 'Cobertura' : 'Coverage', wide: true }, { key: 'limit', label: es ? 'Límite $' : 'Limit $', type: 'number' }, { key: 'page', label: 'Pág.', type: 'number', narrow: true }]} />

          <div className="flow-block-label">{es ? 'Deducibles' : 'Deductibles'}</div>
          <ListEditor es={es} items={editing.deductibles} onChange={v => set('deductibles', v)} addLabel={es ? 'Agregar deducible' : 'Add deductible'} empty={es ? 'Sin deducibles.' : 'No deductibles.'}
            columns={[{ key: 'type', label: es ? 'Tipo' : 'Type', wide: true }, { key: 'amount', label: es ? 'Monto $' : 'Amount $', type: 'number' }, { key: 'percent', label: '%', type: 'number', narrow: true }, { key: 'page', label: 'Pág.', type: 'number', narrow: true }]} />

          <div className="flow-block-label">Mortgage</div>
          <ListEditor es={es} items={editing.mortgagees} onChange={v => set('mortgagees', v)} addLabel={es ? 'Agregar mortgage' : 'Add mortgagee'} empty={es ? 'Sin mortgage.' : 'No mortgagee.'}
            columns={[{ key: 'name', label: es ? 'Compañía' : 'Company', wide: true }, { key: 'loanNumber', label: es ? 'Préstamo #' : 'Loan #' }]} />

          {CLAUSE_GROUPS.map(group => (
            <details key={group} className="policy-details">
              <summary>{t(CLAUSE_LABELS[group])} ({editing[group].length})</summary>
              <ListEditor es={es} items={editing[group]} onChange={v => set(group, v)} addLabel={es ? 'Agregar' : 'Add'} empty={es ? 'Nada cargado.' : 'Nothing yet.'}
                columns={[{ key: 'title', label: es ? 'Cláusula' : 'Clause', wide: true }, { key: 'page', label: 'Pág.', type: 'number', narrow: true }, { key: 'summary', label: es ? 'Qué dice / cómo afecta' : 'What it says / impact', multiline: true, wide: true }]} />
            </details>
          ))}

          <div className="flow-actions">
            <button type="button" className="cd-btn cd-btn-check" disabled={Boolean(busy)} onClick={confirm}>
              <i className="fa-solid fa-circle-check"></i> {busy === 'confirm' ? (es ? 'Confirmando…' : 'Confirming…') : (es ? 'Confirmar revisión y pasar al reclamo' : 'Confirm review & copy to claim')}
            </button>
            <button type="button" className="cd-btn" disabled={Boolean(busy)} onClick={saveDraft}>{es ? 'Guardar borrador' : 'Save draft'}</button>
            <button type="button" className="cd-btn" disabled={Boolean(busy)} onClick={() => setEditing(null)}>{es ? 'Cerrar' : 'Close'}</button>
          </div>
          <div className="flow-note">
            {es
              ? 'Al confirmar, aseguradora, póliza, vigencia, coberturas A–D, deducible, agente y mortgage se copian al reclamo (lo vacío no borra lo que ya tenía).'
              : 'Confirming copies insurance, policy, term, coverages A–D, deductible, agent and mortgage to the claim (empty values do not erase existing data).'}
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyReview;
